import React, { useEffect, useState } from 'react';
import {
  FileText,
  Plus,
  Search,
  Trash2,
  Edit3,
  Copy,
  Sparkles,
  CheckCircle2,
  XCircle,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { apiClient } from '../services/api-client';
import { useAuth } from '../context/AuthContext';

interface TemplateItem {
  id: string;
  name: string;
  subject: string | null;
  htmlContent: string;
  textContent: string | null;
  category: string;
  tags: string[];
  updatedAt: string;
}

const STARTER_TEMPLATES = [
  {
    name: 'Sovereign Welcome Sequence',
    subject: 'Welcome to {{company}} — Your Sovereign Email Node is Ready',
    category: 'Onboarding',
    tags: ['Welcome', 'Onboarding'],
    htmlContent: `<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 32px 24px; background-color: #0f172a; color: #f8fafc; border-radius: 16px; border: 1px solid #1e293b;">
  <div style="border-bottom: 1px solid #334155; padding-bottom: 20px; margin-bottom: 24px;">
    <h1 style="color: #6366f1; font-size: 24px; margin: 0; font-weight: 700;">Antigravity Mail</h1>
    <p style="color: #94a3b8; font-size: 13px; margin-top: 4px;">Sovereign High-Throughput Delivery</p>
  </div>
  <p style="font-size: 16px; line-height: 24px;">Hi {{firstName}},</p>
  <p style="font-size: 15px; line-height: 24px; color: #cbd5e1;">Welcome to your private Stalwart email infrastructure. Your dedicated node is now actively routing outbound and inbound traffic with zero 3rd-party SaaS lock-in.</p>
  <div style="background-color: #1e293b; border-left: 4px solid #6366f1; padding: 16px; border-radius: 8px; margin: 24px 0;">
    <p style="margin: 0; font-size: 14px; color: #e2e8f0; font-weight: 600;">Account Details:</p>
    <p style="margin: 4px 0 0; font-size: 13px; color: #94a3b8;">Recipient: {{email}} | Company: {{company}}</p>
  </div>
  <a href="https://app-staging.axiino.com/webmail" style="display: inline-block; background-color: #4f46e5; color: #ffffff; padding: 12px 24px; border-radius: 10px; text-decoration: none; font-weight: 600; font-size: 14px;">Open Sovereign Webmail</a>
  <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #334155; font-size: 12px; color: #64748b;">
    <p style="margin: 0;">You received this email because you registered on {{company}}.<br><a href="{{unsubscribeUrl}}" style="color: #818cf8; text-decoration: underline;">Unsubscribe</a></p>
  </div>
</div>`,
  },
  {
    name: 'B2B Cold Outreach Pro',
    subject: 'Quick question regarding sovereign email at {{company}}',
    category: 'Cold Outreach',
    tags: ['B2B', 'Sales'],
    htmlContent: `<div style="font-family: Arial, sans-serif; max-width: 580px; margin: 0 auto; color: #1e293b; line-height: 1.6; font-size: 15px;">
  <p>Hi {{firstName}},</p>
  <p>I noticed {{company}} is scaling outreach and wanted to see how you're managing deliverability and mailbox reputation right now.</p>
  <p>We recently helped teams migrate from third-party SMTP limits to an in-house sovereign cluster—achieving 99.4% inbox placement without paying per-seat SaaS costs.</p>
  <p>Do you have 5 minutes this Thursday to see how it works?</p>
  <p style="margin-top: 24px;">Best regards,<br><strong>Outreach Specialist</strong><br>Sovereign Email Team</p>
  <p style="font-size: 11px; color: #94a3b8; margin-top: 32px;"><a href="{{unsubscribeUrl}}" style="color: #94a3b8;">Opt out of future communications</a></p>
</div>`,
  },
];

export const TemplatesPage: React.FC = () => {
  const { currentOrg } = useAuth();
  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [search, setSearch] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('ALL');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Editor Modal State
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formName, setFormName] = useState<string>('');
  const [formSubject, setFormSubject] = useState<string>('');
  const [formCategory, setFormCategory] = useState<string>('Marketing');
  const [formTags, setFormTags] = useState<string>('');
  const [formHtml, setFormHtml] = useState<string>('');
  const [previewMode, setPreviewMode] = useState<'both' | 'edit' | 'preview'>('both');
  const [saving, setSaving] = useState<boolean>(false);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const data = await apiClient.getTemplates();
      setTemplates(data || []);
    } catch (err: any) {
      console.error('Failed to load templates:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [currentOrg?.id]);

  const handleOpenCreate = (starter?: typeof STARTER_TEMPLATES[0]) => {
    setEditingId(null);
    setFormName(starter ? `${starter.name} (Copy)` : '');
    setFormSubject(starter ? starter.subject : '');
    setFormCategory(starter ? starter.category : 'Marketing');
    setFormTags(starter ? starter.tags.join(', ') : 'Newsletter');
    setFormHtml(starter ? starter.htmlContent : '<div style="font-family: sans-serif; padding: 20px;">\n  <h2>Hello {{firstName}},</h2>\n  <p>Write your email content here...</p>\n</div>');
    setShowModal(true);
  };

  const handleOpenEdit = (t: TemplateItem) => {
    setEditingId(t.id);
    setFormName(t.name);
    setFormSubject(t.subject || '');
    setFormCategory(t.category);
    setFormTags((t.tags || []).join(', '));
    setFormHtml(t.htmlContent);
    setShowModal(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const tagsArray = formTags.split(',').map((t) => t.trim()).filter(Boolean);
      if (editingId) {
        await apiClient.updateTemplate(editingId, {
          name: formName,
          subject: formSubject,
          category: formCategory,
          tags: tagsArray,
          htmlContent: formHtml,
        });
        setMessage({ type: 'success', text: 'Template updated successfully!' });
      } else {
        await apiClient.createTemplate({
          name: formName,
          subject: formSubject,
          category: formCategory,
          tags: tagsArray,
          htmlContent: formHtml,
        });
        setMessage({ type: 'success', text: 'Template created successfully!' });
      }
      setShowModal(false);
      await fetchTemplates();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to save template' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Delete template "${name}"?`)) return;
    try {
      await apiClient.deleteTemplate(id);
      setMessage({ type: 'success', text: `Template "${name}" deleted` });
      await fetchTemplates();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to delete template' });
    }
  };

  const insertVariable = (varName: string) => {
    setFormHtml((prev) => prev + varName);
  };

  const filteredTemplates = templates.filter((t) => {
    const matchesSearch = t.name.toLowerCase().includes(search.toLowerCase()) || (t.subject || '').toLowerCase().includes(search.toLowerCase());
    const matchesCat = selectedCategory === 'ALL' || t.category === selectedCategory;
    return matchesSearch && matchesCat;
  });

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-800">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2.5">
            <FileText className="w-6 h-6 text-brand-400" />
            <span>Email Templates Library</span>
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Author and manage reusable HTML marketing templates with dynamic personalization tags (&#123;&#123;firstName&#125;&#125;, &#123;&#123;company&#125;&#125;).
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchTemplates}
            disabled={loading}
            className="p-2.5 rounded-xl border border-slate-700 bg-slate-900/80 hover:bg-slate-800 text-slate-300 transition"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => handleOpenCreate()}
            className="px-4 py-2.5 rounded-xl text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white shadow-lg shadow-brand-950/40 transition flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Create Blank Template
          </button>
        </div>
      </div>

      {/* Status Alert */}
      {message && (
        <div
          className={`p-3.5 rounded-xl text-xs flex items-center justify-between border ${
            message.type === 'success'
              ? 'bg-emerald-950/40 border-emerald-800/80 text-emerald-300'
              : 'bg-rose-950/40 border-rose-800/80 text-rose-300'
          }`}
        >
          <div className="flex items-center gap-2">
            {message.type === 'success' ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <XCircle className="w-4 h-4 text-rose-400" />}
            <span>{message.text}</span>
          </div>
          <button onClick={() => setMessage(null)} className="text-slate-400 hover:text-white">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Starter Templates Banner */}
      <div className="bg-gradient-to-r from-brand-950/50 via-slate-900/40 to-purple-950/30 border border-brand-800/40 p-4 rounded-2xl shadow-lg">
        <div className="flex items-center gap-2 mb-3 text-xs font-bold text-brand-300 uppercase tracking-wider">
          <Sparkles className="w-4 h-4 text-brand-400" />
          <span>Pre-made Responsive Starter Templates</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {STARTER_TEMPLATES.map((starter, idx) => (
            <div
              key={idx}
              className="p-3.5 bg-slate-900/80 border border-slate-800 rounded-xl flex items-center justify-between hover:border-brand-700/60 transition group"
            >
              <div>
                <h4 className="text-xs font-bold text-white group-hover:text-brand-300 transition">{starter.name}</h4>
                <p className="text-[11px] text-slate-400 mt-0.5 max-w-sm truncate">{starter.subject}</p>
                <div className="flex items-center gap-1.5 mt-2">
                  <span className="text-[10px] px-2 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700">
                    {starter.category}
                  </span>
                </div>
              </div>
              <button
                onClick={() => handleOpenCreate(starter)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-800 hover:bg-brand-600 text-slate-200 hover:text-white border border-slate-700 hover:border-brand-500 transition flex items-center gap-1.5"
              >
                <Copy className="w-3.5 h-3.5" />
                Use Template
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md w-full">
          <Search className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search templates by title or subject..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-xl bg-slate-900/60 border border-slate-800 text-xs text-white focus:outline-none focus:border-brand-500 placeholder-slate-500"
          />
        </div>

        <div className="flex items-center gap-2 overflow-x-auto w-full sm:w-auto">
          {['ALL', 'Marketing', 'Onboarding', 'Cold Outreach', 'Newsletter'].map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition border ${
                selectedCategory === cat
                  ? 'bg-brand-600 text-white border-brand-500 shadow-md shadow-brand-500/20'
                  : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="p-16 text-center text-slate-400 flex flex-col items-center justify-center gap-3">
          <Loader2 className="w-6 h-6 animate-spin text-brand-400" />
          <span className="text-xs">Loading templates...</span>
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="p-16 text-center text-slate-400 space-y-3 bg-slate-900/30 border border-slate-800/80 rounded-2xl">
          <FileText className="w-10 h-10 text-slate-600 mx-auto" />
          <h3 className="text-sm font-bold text-white">No Email Templates Found</h3>
          <p className="text-xs text-slate-500 max-w-md mx-auto">
            You haven't created any templates yet. Click above to use one of the starter templates or create a blank one.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map((template) => (
            <div
              key={template.id}
              className="bg-slate-900/50 border border-slate-800 rounded-2xl p-4 flex flex-col justify-between hover:border-slate-700 transition shadow-lg group"
            >
              <div>
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-sm font-bold text-white group-hover:text-brand-300 transition line-clamp-1">
                    {template.name}
                  </h3>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-800 text-brand-300 border border-slate-700 whitespace-nowrap">
                    {template.category}
                  </span>
                </div>
                <p className="text-xs text-slate-400 mt-1 line-clamp-2 italic font-serif">
                  {template.subject || 'No subject line specified'}
                </p>

                <div className="flex flex-wrap gap-1 mt-3">
                  {(template.tags || []).map((tag, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 rounded bg-slate-950 text-slate-400 border border-slate-800/80">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Preview Window Box */}
              <div className="mt-4 p-2.5 bg-slate-950 rounded-xl border border-slate-800/80 max-h-28 overflow-hidden relative">
                <div
                  className="text-[10px] text-slate-400 scale-[0.85] origin-top-left pointer-events-none"
                  dangerouslySetInnerHTML={{ __html: template.htmlContent.substring(0, 300) + '...' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent" />
              </div>

              {/* Actions Footer */}
              <div className="flex items-center justify-between pt-3 mt-3 border-t border-slate-800/60">
                <span className="text-[10px] text-slate-500">
                  Updated {new Date(template.updatedAt).toLocaleDateString()}
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => handleOpenEdit(template)}
                    className="p-1.5 rounded-lg bg-slate-800 hover:bg-brand-600 text-slate-300 hover:text-white transition"
                    title="Edit Template"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleDelete(template.id, template.name)}
                    className="p-1.5 rounded-lg bg-slate-800 hover:bg-rose-900 text-slate-300 hover:text-rose-300 transition"
                    title="Delete Template"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-5xl my-6 p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-brand-400" />
                {editingId ? 'Edit Email Template' : 'Author New Email Template'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-white">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Template Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Q4 Growth Sequence - Step 1"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white focus:outline-none focus:border-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Category</label>
                  <select
                    value={formCategory}
                    onChange={(e) => setFormCategory(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white focus:outline-none focus:border-brand-500"
                  >
                    <option value="Marketing">Marketing</option>
                    <option value="Onboarding">Onboarding</option>
                    <option value="Cold Outreach">Cold Outreach</option>
                    <option value="Newsletter">Newsletter</option>
                    <option value="Transactional">Transactional</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">Default Subject Line</label>
                <input
                  type="text"
                  placeholder="e.g. Hello {{firstName}}, exciting news from {{company}}"
                  value={formSubject}
                  onChange={(e) => setFormSubject(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white focus:outline-none focus:border-brand-500 font-mono"
                />
              </div>

              {/* Personalization Tag Pills */}
              <div className="p-3 bg-slate-950/80 rounded-xl border border-slate-800 flex flex-wrap items-center gap-2">
                <span className="text-[11px] font-bold text-slate-400 mr-2 flex items-center gap-1">
                  <Sparkles className="w-3.5 h-3.5 text-brand-400" /> Insert Variable:
                </span>
                {['{{firstName}}', '{{lastName}}', '{{company}}', '{{email}}', '{{unsubscribeUrl}}'].map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => insertVariable(tag)}
                    className="px-2.5 py-1 rounded-lg bg-slate-800 hover:bg-brand-600 text-brand-300 hover:text-white border border-slate-700 text-xs font-mono font-semibold transition"
                  >
                    {tag}
                  </button>
                ))}
              </div>

              {/* View Switcher */}
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-300">HTML Source & Live Preview</span>
                <div className="flex items-center gap-1 bg-slate-950 p-1 rounded-lg border border-slate-800">
                  <button
                    type="button"
                    onClick={() => setPreviewMode('edit')}
                    className={`px-2.5 py-1 rounded text-[11px] font-semibold transition ${previewMode === 'edit' ? 'bg-brand-600 text-white' : 'text-slate-400'}`}
                  >
                    HTML Code
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewMode('both')}
                    className={`px-2.5 py-1 rounded text-[11px] font-semibold transition ${previewMode === 'both' ? 'bg-brand-600 text-white' : 'text-slate-400'}`}
                  >
                    Split View
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewMode('preview')}
                    className={`px-2.5 py-1 rounded text-[11px] font-semibold transition ${previewMode === 'preview' ? 'bg-brand-600 text-white' : 'text-slate-400'}`}
                  >
                    Live Preview
                  </button>
                </div>
              </div>

              {/* Split Editor / Preview Panes */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-96">
                {(previewMode === 'edit' || previewMode === 'both') && (
                  <div className={`h-full flex flex-col ${previewMode === 'edit' ? 'col-span-2' : ''}`}>
                    <textarea
                      required
                      value={formHtml}
                      onChange={(e) => setFormHtml(e.target.value)}
                      placeholder="Enter HTML template markup..."
                      className="w-full h-full p-4 rounded-xl bg-slate-950 border border-slate-800 text-xs text-slate-200 font-mono focus:outline-none focus:border-brand-500 resize-none"
                    />
                  </div>
                )}

                {(previewMode === 'preview' || previewMode === 'both') && (
                  <div className={`h-full bg-white rounded-xl overflow-y-auto p-4 border border-slate-300 ${previewMode === 'preview' ? 'col-span-2' : ''}`}>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: formHtml
                          .replace(/{{firstName}}/g, 'Alex')
                          .replace(/{{lastName}}/g, 'Morgan')
                          .replace(/{{company}}/g, 'Acme Corp')
                          .replace(/{{email}}/g, 'alex.morgan@acme.test')
                          .replace(/{{unsubscribeUrl}}/g, '#'),
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 rounded-xl text-xs font-medium bg-slate-800 text-slate-300 hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2 rounded-xl text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white flex items-center gap-2 shadow-lg shadow-brand-950/40"
                >
                  {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  {editingId ? 'Save Changes' : 'Create Template'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
